import { companyHidesPriorAuth } from "@/composables/usePriorAuthHidden";
const MODULE_ORDER = ["intake", "bv", "medical_necessity", "prior_auth", "appeals"];
const CHIP_MODULES = new Set(["prior_auth", "intake"]);
const STEP_SECTIONS = [
    ["bv", "benefits_verification"],
    ["medical_necessity", "clinical_review"],
];
const STEP_TYPES = new Set(["bv", "medical_necessity"]);
const INTAKE_TYPES = new Set(["intake", "bv", "medical_necessity"]);
const SECTION_LABELS = {
    all: "Worklist",
    prior_auth: "Prior Auth",
    intake: "Intake",
    bv: "Benefit Verification",
    medical_necessity: "Medical Necessity",
    appeals: "Appeals",
};
const CHIP_LABELS = {
    prior_auth: "Prior Auth",
    intake: "Intake",
    bv: "BV",
    medical_necessity: "Med Necessity",
    appeals: "Appeals",
};
const SECTION_ICONS = {
    all: "mdi-format-list-checks",
    prior_auth: "mdi-file-document-check-outline",
    intake: "mdi-tray-arrow-down",
    bv: "mdi-shield-check-outline",
    medical_necessity: "mdi-stethoscope",
    appeals: "mdi-file-document-alert-outline",
};
const INTAKE_MODULES = ["fax_intake", "benefits_verification", "clinical_review", "necessity_check"];
const STEP_MODULES = ["benefits_verification", "clinical_review"];
const PRIOR_AUTH_PAGES = new Set(["prior-auth", "all-submissions"]);
const LIST_PAGES = new Set(["prior-auth", "all-submissions", "intake", "all-patients"]);
function showsIntake(modules) {
    return modules.some((key) => INTAKE_MODULES.includes(key));
}
/** Two or more of Prior Auth, Intake, BV, MN and Appeals get the Worklist; with one, that module's own page is the page. */
export function worklistAppliesTo(modules) {
    const steps = STEP_MODULES.filter((key) => modules.includes(key)).length;
    return (Number(modules.includes("prior_auth")) +
        Number(showsIntake(modules)) +
        steps +
        Number(modules.includes("appeals")) >=
        2);
}
/** The Worklist is on for this company: the pilot flag plus two or more modules. */
export function worklistShownFor(enabled, modules) {
    return Boolean(enabled) && worklistAppliesTo(modules ?? []);
}
/** The company's home: its configured page, Intake when Prior Auth is off, the Worklist when neither shows. */
export function homePageFor(configured, modules) {
    const page = configured || "prior-auth";
    if (!modules?.length)
        return page;
    const hidesPriorAuth = companyHidesPriorAuth(modules);
    if (hidesPriorAuth && !modules.includes("fax_intake") && LIST_PAGES.has(page))
        return "worklist";
    return PRIOR_AUTH_PAGES.has(page) && hidesPriorAuth ? "intake" : page;
}
/** Prior auth, intake or both, from the modules the company shows. */
export function companyAudienceFor(modules) {
    const intake = showsIntake(modules);
    const pa = modules.includes("prior_auth");
    if (pa && !intake)
        return "prior_auth";
    if (intake && !pa)
        return "intake";
    return "all";
}
/** The work types a Worklist URL is narrowed to, in a fixed order; none means all work. */
export function typesFrom(type) {
    const picked = typeof type === "string" ? type.split(",") : [];
    return MODULE_ORDER.filter((key) => picked.includes(key));
}
/** The layout a Worklist URL shows: a single-module company's own, else the one tab its work types add up to. */
export function audienceFor(company, type) {
    if (company !== "all")
        return company;
    const types = typesFrom(type);
    if (types.length === 1 && types[0] === "prior_auth")
        return "prior_auth";
    if (types.length && types.every((key) => INTAKE_TYPES.has(key)))
        return "intake";
    return "all";
}
/** Toggle one module chip; Intake already holds BV and Medical Necessity, so it and they replace each
 * other. Appeals is a layout of its own — the only one its own columns and filters describe — so it
 * is exclusive: picking it drops the rest, and picking anything else drops it. */
export function toggledTypes(current, key) {
    if (current.includes(key))
        return current.filter((type) => type !== key);
    if (key === "appeals")
        return ["appeals"];
    const kept = current.filter((type) => type !== "appeals" && (key === "intake" ? !STEP_TYPES.has(type) : !STEP_TYPES.has(key) || type !== "intake"));
    return MODULE_ORDER.filter((type) => type === key || kept.includes(type));
}
function section(key, query) {
    return { key, label: SECTION_LABELS[key], icon: SECTION_ICONS[key], query };
}
/** The fan-out under Worklist: the general list first, then each view in workflow order, Appeals last. */
export function sectionsFor(modules) {
    const company = companyAudienceFor(modules);
    const sections = [section("all", {})];
    if (company === "all")
        sections.push(section("intake", { type: "intake" }));
    if (company !== "prior_auth") {
        for (const [key, module] of STEP_SECTIONS) {
            if (modules.includes(module))
                sections.push(section(key, { type: key }));
        }
    }
    if (company === "all")
        sections.push(section("prior_auth", { type: "prior_auth" }));
    // Appeals keeps no Action Needed statuses, so its link asks for the whole list rather than the chip's default.
    if (modules.includes("appeals"))
        sections.push(section("appeals", { type: "appeals", view: "all" }));
    return sections;
}
/** The Worklist view of a company's prior auth work: its Prior Auth section, else the Worklist itself. */
export function priorAuthWorklistRoute(modules) {
    return { name: "worklist", query: sectionsFor(modules).find((entry) => entry.key === "prior_auth")?.query ?? {} };
}
/** The header's module chips: Intake and Prior Auth only for now (BV and Medical Necessity stay Intake statuses). */
export function chipSectionsFor(modules) {
    return sectionsFor(modules).flatMap((entry) => entry.key !== "all" && CHIP_MODULES.has(entry.key) ? [{ key: entry.key, label: CHIP_LABELS[entry.key] }] : []);
}
/** A BV or MN section lists one step's work: no module chips, and only the order's own step needs action there. */
export function isStepSection(section) {
    return STEP_TYPES.has(section);
}
/** The module a BV or MN section lists the steps of; null for every other section. */
export function stepModuleFor(section) {
    return STEP_SECTIONS.find(([key]) => key === section)?.[1] ?? null;
}
/** Which fan-out entry a Worklist URL is showing: Appeals, or a BV or MN section, is its one work type. */
export function activeSectionFor(modules, query) {
    const company = companyAudienceFor(modules);
    const types = typesFrom(query.type);
    if (types.length === 1 && types[0] === "appeals" && modules.includes("appeals"))
        return "appeals";
    if (company !== "prior_auth" && types.length === 1 && STEP_TYPES.has(types[0]))
        return types[0];
    const audience = audienceFor(company, query.type);
    return company === "all" ? audience : "all";
}
export function sectionLabel(key) {
    return SECTION_LABELS[key];
}
